/**
 * Safe parsing and normalization of Referer / document.referrer values.
 *
 * - Treat as untrusted hints (spoofable, often missing, Referrer-Policy may strip).
 * - Only http(s) origins; blocks javascript:, data:, etc.
 * - Enforces length and optional host allowlist when options.allowedHosts is set.
 */

export const REFERER_MAX_LENGTH = 2048

export type ProcessedReferer = {
    /** Fully qualified URL string, origin + path + search (no hash). */
    href: string
    origin: string
    hostname: string
    pathname: string
    search: string
}

export type ProcessRefererOptions = {
    maxLength?: number
    /**
     * If set, only these hostnames (lowercase, no port) are accepted.
     * Omit or empty = allow any http(s) host.
     */
    allowedHosts?: Set<string> | null
}

function normalizeHostname(host: string): string {
    return host.trim().toLowerCase().replace(/:\d+$/, '')
}

/**
 * Parse and validate a raw Referer or document.referrer string.
 */
export function processRefererString(
    raw: string | undefined | null,
    options: ProcessRefererOptions = {},
): ProcessedReferer | null {
    if (raw == null || typeof raw !== 'string') {
        return null
    }

    const trimmed = raw.trim()
    const maxLength = options.maxLength ?? REFERER_MAX_LENGTH
    if (trimmed.length === 0 || trimmed.length > maxLength) {
        return null
    }

    let parsed: URL
    try {
        parsed = new URL(trimmed)
    } catch {
        return null
    }

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return null
    }

    const hostname = normalizeHostname(parsed.hostname)
    if (!hostname) {
        return null
    }

    const allow = options.allowedHosts
    if (allow && allow.size > 0 && !allow.has(hostname)) {
        return null
    }

    parsed.hash = ''

    return {
        href: parsed.href,
        origin: parsed.origin,
        hostname,
        pathname: parsed.pathname,
        search: parsed.search,
    }
}
